import type { CSSProperties } from "react"
import { cx } from "./cx"
import "./beam.css"

export type BeamState = "idle" | "listening" | "working" | "done" | "reading" | "static"

export interface BeamProps {
  /** idle: 7s breathe between -12° and -8°. listening: ±3° sway. working: settle swing, loops. done: comes to rest once. reading: small, grey, still. static: no motion. */
  state?: BeamState
  /** bar = 22px beside the wordmark; hero = 56px on its own. */
  size?: "bar" | "hero"
  /** On navy or a dark photo the fulcrum dot takes on-navy. */
  on?: "navy" | "dark"
  /** Accessible name; defaults to "Nira is working" / "Nira is listening", else decorative. */
  label?: string | null
  /** Degrees the bar comes to rest at in done and static (the answer's lean); -10 by default. */
  lean?: number
  className?: string
  style?: CSSProperties
}

/** The mark: a 56-unit box, bar from (8,26) to (48,26), fulcrum at (28,36). Rotation is about (28,26). */
function BeamSvg() {
  return (
    <svg viewBox="0 0 56 56" aria-hidden="true" focusable="false">
      <line className="nr-beam-bar" x1={8} y1={26} x2={48} y2={26} />
      <circle className="nr-beam-dot" cx={28} cy={36} r={4.5} />
    </svg>
  )
}

export function Beam({ state = "idle", size = "bar", on, label, lean, className, style }: BeamProps) {
  const name =
    label !== undefined ? label : state === "working" ? "Nira is working" : state === "listening" ? "Nira is listening" : null
  return (
    <span
      className={cx("nr", "nr-beam", className)}
      data-state={state}
      data-size={size}
      data-on={on}
      role={name ? "img" : undefined}
      aria-label={name || undefined}
      aria-hidden={name ? undefined : true}
      style={lean === undefined ? style : ({ ...style, "--nr-lean": `${lean}deg` } as CSSProperties)}
    >
      <BeamSvg />
    </span>
  )
}

export interface WordmarkProps {
  state?: BeamState
  href?: string
  on?: "navy"
  className?: string
}

export function Wordmark({ state = "idle", href, on, className }: WordmarkProps) {
  const body = (
    <>
      <Beam state={state} on={on} label={null} />
      <span className="nr-wordmark-text" aria-hidden="true">
        nira
      </span>
    </>
  )
  return href ? (
    <a className={cx("nr", "nr-wordmark", className)} href={href} data-on={on} aria-label="Nira">
      {body}
    </a>
  ) : (
    <span className={cx("nr", "nr-wordmark", className)} data-on={on} aria-label="Nira">
      {body}
    </span>
  )
}

export type LeanKind = "helps" | "hurts" | "mixed"
const LEAN_WORD: Record<LeanKind, string> = { helps: "Helps", hurts: "Hurts", mixed: "Mixed" }

export interface LeanProps {
  lean: LeanKind
  /** Defaults to Helps / Hurts / Mixed. Pass null to show the glyph alone (only beside text that already says it). */
  label?: string | null
  count?: number
  size?: "lg"
  title?: string
  className?: string
}

/** The tilted mini-beam that says which way a decision leans. Never colour alone: the tilt and the word carry it. */
export function Lean({ lean = "mixed", label, count, size, title, className }: LeanProps) {
  const word = label !== undefined ? label : LEAN_WORD[lean]
  return (
    <span className={cx("nr", "nr-lean", className)} data-lean={lean} data-size={size} title={title}>
      <svg viewBox="0 0 56 56" aria-hidden="true" focusable="false">
        <line className="nr-lean-bar" x1={8} y1={26} x2={48} y2={26} />
        <circle className="nr-lean-dot" cx={28} cy={36} r={7} />
      </svg>
      {word ? <span>{word}</span> : null}
      {count !== undefined ? (
        <span className="nr-mono" style={{ fontWeight: 500 }}>
          {String(count)}
        </span>
      ) : null}
    </span>
  )
}

export { LEAN_WORD }
